/*
* Create client invite view for authenticated users
*/

import { filterBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { goBack, push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import ApiEndpoints from '../config/apiendpoints';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import { UserUtilities } from '../user/user-utilities';

export const PortalRoles = {
    PortalAdmin: {
        RoleName: 'PortalAdmin',
        RoleText: 'User Manager',
    },
    PortalUser: {
        RoleName: 'PortalUser',
        RoleText: 'User',
    },
};

export const PortalAccessActions = {
    Disable: 'disable',
    Enable: 'enable',
    Modify: 'modify'
};

export const DefaultNewInviteExpirationDate = (): Date => moment().add(6, 'months').startOf('day').toDate();
export const MinimumNewInviteExpirationDate = (): Date => moment().startOf('year').toDate();
export const MaximumNewInviteExpirationDate = (): Date => moment().add(100, 'years').startOf('day').toDate();

export interface ICreateInviteProps extends AppComp.IAppComponentProps {
    newClientInvite?: any;
    navigateTo?: any;
    navigateBack?: any;
    users?: any;
    associations?: any;
    associationId?: any;
    portalUsers?: any;
    tempPassword?: any;
}

export interface ICreateInviteState extends AppComp.IAppComponentState {
    formChanges?: any;
    showModalCreateClientInvite?: boolean;
    autoShowModal?: boolean;
    skipAssociation?: any;
    filterAssociation?: any;
}

class CreateInvite extends AppComp.AppComponent<ICreateInviteProps, ICreateInviteState> {
    public originalFormDataCache = {};

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: ICreateInviteProps) {
        super(props, 'app.portal-invites-add.');

        this.state = {
            formChanges: {},
            showModalCreateClientInvite: false,
            skipAssociation: 0,
            autoShowModal: true,
            filterAssociation: undefined,
        };
    }

    public componentWillMount() {
        const autoShow = (_.get(this.state, 'autoShowModal')) ? true : false;

        if (autoShow) {
            this.handleAutoShowModal();
        }
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // HANDLERS    
    public handleAutoShowModal(autoShow: boolean = false) {
        let formChanges = _.cloneDeep(this.state.formChanges);
        const newClientInvite = this.getDefaultClientInviteObject([]);

        _.set(formChanges, 'newClientInvite', newClientInvite);

        this.setState({ formChanges: formChanges, showModalCreateClientInvite: true, autoShowModal: autoShow });
    }

    public handleShowModalCreateInvite(e: any, formData: any, autoShow: boolean = false) {
        let formChanges = _.cloneDeep(this.state.formChanges);
        const newClientInvite = this.getDefaultClientInviteObject(_.get(formData, 'associationOptions', []));

        _.set(formChanges, 'newClientInvite', newClientInvite);

        this.setState({ formChanges: formChanges, showModalCreateClientInvite: true, autoShowModal: autoShow });
    }

    public handleCloseModalCreateInvite() {
        this.setState({ formChanges: {}, showModalCreateClientInvite: false });

        this.props.navigateBack();
    }

    public handleClearExpirationDateButton(e: any, formData: any) {
        let formChanges = _.cloneDeep(this.state.formChanges);

        _.set(formChanges, 'newClientInvite.expirationDate', null);

        this.setState({ formChanges: formChanges });
    }

    public handleCreateClientInviteButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const { associations, users, associationId, portalUsers, tempPassword, ...rest } = this.props;
        const formData = this.getFormData(associations, users, portalUsers, associationId, tempPassword);

        let payload = _.cloneDeep(_.get(this.state, 'formChanges.newClientInvite'));

        if (_.get(formData, 'assignedAssociation')) {
            _.set(payload, 'associationsAssigned', [_.first(_.get(formData, 'associationOptions', []))]);
        }

        if (_.get(formData, 'tempPassword')) {
            _.set(payload, 'tempPassword', Formatters.getStr(formData, 'tempPassword'));
        }

        const changeData = {
            newClientInvite: payload
        };

        this.props.newClientInvite(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                this.setState({ formChanges: {}, showModalCreateClientInvite: false });

                const userObjId = _.get(response, 'body.user.document.userObjectId', '');
                if (associationId) {
                    this.props.navigateTo(NavigationLinks.portalAssociationReport(associationId));
                } else {
                    this.props.navigateTo(NavigationLinks.userDetailObjId(userObjId));
                }
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                case 'email':
                case 'password':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            // if (String(newValue) !== String(origVal)) {
            // New value
            _.set(formChangesCopy, fieldName, newValue);
            _.set(formChangesResult, fieldName, newValue);
            applyChange = true;
            // } else {
            //     // Same as orig val
            //     if (_.has(formChangesCopy, fieldName)) {
            //         _.unset(formChangesCopy, fieldName);
            //         _.set(formChangesResult, fieldName, newValue);
            //         applyChange = true;
            //     }
            // }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public getFormData(associations: any, users: any, portalInvites: any, associationId: any = 0, tempPassword: any = '') {
        // associationoptions should be limited to only those that the current user has portaladmin roles        
        const assignedAssociation = _.find(associations, { 'associationId': associationId });
        const availableAssociations = (associationId) ? [assignedAssociation] : associations;
        let associationOptions = _.orderBy(availableAssociations, [a => a.name.toLowerCase()], ['asc']).map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.composeAssociationNameLabel(c, true) }));

        if (this.state.filterAssociation && !associationId) {
            associationOptions = filterBy(associationOptions, this.state.filterAssociation);
        }

        let portalRoleOptions = _.stubArray();

        // If staff or portaladmin role, allow setting portaladmin role
        const auth = new Auth();
        const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;
        const isClient = auth.isClient();
        const userObjId = auth.getSessionUserObjectId();

        let canAddPortalAdmin = (isStaff) ? true : false;
        let canAddPortalUser = (isStaff) ? true : false;

        if (!isStaff && isClient && _.size(portalInvites)) {
            // Does current user have client roles and PortalAdmin role for this assoc?
            let isClientPortalAdmin = false;
            let activePortalInvites: any = UserUtilities.GetValidPortalInvites(portalInvites, associationId);
            if (_.size(activePortalInvites)) {
                const filterAdminUser = UserUtilities.FilterPortalInvites(activePortalInvites, PortalRoles.PortalAdmin.RoleName);
                isClientPortalAdmin = (_.find(filterAdminUser, (o) => _.get(o, 'user.userObjectId', '') === userObjId)) ? true : false;
            }
            canAddPortalUser = (isClient && isClientPortalAdmin) ? true : false;
        }

        if (canAddPortalAdmin) {
            portalRoleOptions.push(
                {
                    value: PortalRoles.PortalAdmin.RoleName,
                    text: PortalRoles.PortalAdmin.RoleText,
                }
            );
        }

        if (canAddPortalUser) {
            portalRoleOptions.push(
                {
                    value: PortalRoles.PortalUser.RoleName,
                    text: PortalRoles.PortalUser.RoleText,
                }
            );
        }

        let newClientInvite = _.get(this.state.formChanges, 'newClientInvite', this.getDefaultClientInviteObject(associationOptions));

        let result = {
            newClientInvite: newClientInvite,

            portalRoleOptions: (_.size(portalRoleOptions)) ? portalRoleOptions : [],
            associationOptions: (_.size(associationOptions)) ? associationOptions : [],
            assignedAssociation: assignedAssociation,
            tempPassword: tempPassword,
            users: (_.size(users)) ? users : [],
        };

        const formDataResult = _.merge({}, result, this.state.formChanges);

        return formDataResult;
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        return resetFormData;
    }

    public getDefaultClientInviteObject(associationOptions: any) {
        const associationsAssigned = (_.size(associationOptions) === 1) ? associationOptions : _.stubArray();

        const result = _.cloneDeep({
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            associationsAssigned: associationsAssigned,
            expirationDate: DefaultNewInviteExpirationDate(),
            role: PortalRoles.PortalUser.RoleName
        });

        return result;
    }

    // RENDERERS
    public renderHeaderButtonToolbar = (formData: any) => {
        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={12}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleShowModalCreateInvite(e, formData); }}>
                                        <i className="rs-icon rs-icon-user-plus" /> {this.appText('app.portal-invites.invite-user', false)}
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderCreateInviteModal = (formData: any, showSecuritySection: boolean = false) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsLargeTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 6,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 4,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallDate = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 8,
            className: '',
            format: 'M/dd/yyyy',
            formatPlaceholder: 'formatPattern',
        });

        const inputPropsCheckboxes = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsVirtualizedSelectRole = _.merge({}, defaultInputProps, {
            className: '',
            colInputXs: 6,
        });

        const virtualizedDropdownPageSize = 20;
        const handleAssociationMultiSelectFilterChange = (e: any, f: any) => {
            this.setState({
                skipAssociation: 0,
                filterAssociation: e.filter,
            });
        };

        const handleAssociationMultiSelectPageChange = (e: any, f: any) => {
            this.setState({
                skipAssociation: e.page.skip
            });
        };

        const handleFormAssociationInputChange = (e: any, o: any) => {
            const fieldName = _.get(e, 'target.name', '');
            const newValue = _.get(e, 'target.value', '');

            let prevChanges = this.state.formChanges;
            _.set(prevChanges, 'newClientInvite.associationsAssigned', newValue);

            this.setState({
                formChanges: prevChanges
            });
        };

        const clearExpirationDateButtonVisible = (_.get(formData, 'newClientInvite.expirationDate') === null) ? false : true;
        const email = _.toLower(_.get(formData, 'newClientInvite.email', ''));
        const foundEmail = _.find(formData.users, (o) => email === _.toLower(_.get(o, 'email', '')));
        const showNameInputs = (email && !foundEmail) ? true : false;
        const assocAssigned = (_.size(_.get(formData, 'newClientInvite.associationsAssigned', [])) || _.get(formData, 'assignedAssociation')) ? true : false;

        const emailValid = Formatters.getStr(formData, 'newClientInvite.email') ? true : false;
        const roleValid = Formatters.getStr(formData, 'newClientInvite.role') ? true : false;
        const emailNameValid = ((!foundEmail && Formatters.getStr(formData, 'newClientInvite.firstName') && Formatters.getStr(formData, 'newClientInvite.lastName')) || foundEmail) ? true : false;
        const createInviteFormValid = (
            emailValid &&
            roleValid &&
            emailNameValid &&
            assocAssigned
        ) ? true : false;

        // console.log('modal validation', {emailValid, roleValid, emailNameValid, assocAssigned, formData});

        let buttonCaption = (_.size(_.get(formData, 'newClientInvite.associationsAssigned', [])) <= 1) ? this.appText('create-portal-invite') : this.appText('create-portal-invites');
        buttonCaption = this.appText('app.portal-invites.invite-user', false);

        const assignedAssociations = (_.get(formData, 'assignedAssociation')) ? (
            <KendoWrappers.KendoDisabledInputHidden
                name="_newClientInvite.associationsAssigned"
                label={this.appText('association')}
                value={Formatters.composeAssociationNameLabel(_.get(formData, 'assignedAssociation'))}
                {...inputPropsLargeTextDebounced}
            />
        ) : (
            <KendoWrappers.KendoMultiSelect
                name="newClientInvite.associationsAssigned"
                clearable={true}
                filterable={true}
                onFilterChange={(e) => { handleAssociationMultiSelectFilterChange(e, formData); }}
                label={this.appText('associations')}
                value={_.get(formData, 'newClientInvite.associationsAssigned', [])}
                required={true}
                data={_.get(formData, 'associationOptions', []).slice(this.state.skipAssociation, this.state.skipAssociation + virtualizedDropdownPageSize)}
                dataItemKey="value"
                virtual={{
                    total: _.size(_.get(formData, 'associationOptions', [])),
                    pageSize: virtualizedDropdownPageSize,
                    skip: this.state.skipAssociation,
                }}
                onPageChange={(e) => { handleAssociationMultiSelectPageChange(e, formData); }}
                textField="text"
                onChange={(o) => { handleFormAssociationInputChange({ target: { name: 'newClientInvite.associationsAssigned', type: 'kendo-multiselect', value: _.get(o, 'target.value') } }, o); }}
                {...inputPropsVirtualizedSelect}
            />
        );

        // let temporaryPassword = (<div />);
        // if (!foundEmail) {
        //     temporaryPassword = (
        //         <KendoWrappers.KendoDisabledInputHidden
        //             name="_tempPassword"                    
        //             label={this.appText('modals.create-invite.temporary-password')}
        //             value={<span className="invite-temporary-password">{Formatters.getStr(formData, 'tempPassword')}</span>}                                
        //             {...inputPropsLargeTextDebounced}                    
        //         />    
        //     );
        // }

        let securityPermissionsSection = (<div />);
        if (showSecuritySection) {
            const assignedRoles = (_.size(_.get(formData, 'portalRoleOptions', [])) > 1) ? (
                <KendoWrappers.KendoDropdownListInput
                    name="newClientInvite.role"
                    label={this.appText('security-roles')}
                    labelHelpTooltip={this.appText('security-roles.tooltip')}
                    clearable={false}
                    filterable={false}
                    required={true}
                    data={_.get(formData, 'portalRoleOptions', [])}
                    value={_.get(formData, 'newClientInvite.role', '')}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClientInvite.role', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelectRole}
                />
            ) : (
                <KendoWrappers.KendoDisabledInputHidden
                    name="_newClientInvite.role"
                    label={this.appText('security-roles')}
                    value={_.get(PortalRoles, `${_.get(formData, 'newClientInvite.role', '')}.RoleText`, '')}
                    {...inputPropsLargeTextDebounced}
                />
            );

            securityPermissionsSection = (
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('security-details')}>
                    {assignedRoles}

                    <KendoWrappers.KendoDateInputOptional
                        name="newClientInvite.expirationDate"
                        clearable={true}
                        required={false}
                        min={MinimumNewInviteExpirationDate()}
                        max={MaximumNewInviteExpirationDate()}
                        label={this.appText('security-expiration-date')}
                        labelHelpTooltip={this.appText('security-expiration-date.tooltip')}
                        value={Formatters.formatDateForKendoDateInput(_.get(formData, 'newClientInvite.expirationDate'))}
                        tabIndex={5}
                        {...inputPropsSmallDate}
                    >
                        <div className="edit-order-set-due-date-button-wrapper">
                            <Button
                                hidden={!clearExpirationDateButtonVisible}
                                bsSize="xs"
                                bsStyle="info"
                                onClick={e => { this.handleClearExpirationDateButton(e, formData); }}
                            >
                                <i className="glyphicon glyphicon-remove" /> {this.appText('security-clear-expiration-date')}
                            </Button>
                        </div>
                    </KendoWrappers.KendoDateInputOptional>
                </DetailFormCommon.InventoryDetailFormSection>
            );
        }

        return (
            <Modal enforceFocus={false} show={this.state.showModalCreateClientInvite} onHide={() => this.handleCloseModalCreateInvite()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.create-invite.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="edit-user-new-user-modal invite-user-modal">
                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('user-details')}>
                            <KendoWrappers.KendoTextInputDebounced
                                name="newClientInvite.email"
                                label={this.appText('modals.create-invite.email')}
                                value={Formatters.getStr(formData, 'newClientInvite.email')}
                                placeholder={this.appText('modals.create-invite.required')}
                                type="email"
                                debounceTimeout={1000}
                                required={true}
                                {...inputPropsLargeTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="newClientInvite.firstName"
                                label={this.appText('modals.create-invite.first-name')}
                                value={Formatters.getStr(formData, 'newClientInvite.firstName')}
                                required={showNameInputs}
                                rowClassName={(showNameInputs) ? '' : 'client-invite-hide-name-fields'}
                                {...inputPropsSmallTextDebounced}
                            />

                            <KendoWrappers.KendoTextInputDebounced
                                name="newClientInvite.lastName"
                                label={this.appText('modals.create-invite.last-name')}
                                value={Formatters.getStr(formData, 'newClientInvite.lastName')}
                                required={showNameInputs}
                                rowClassName={(showNameInputs) ? '' : 'client-invite-hide-name-fields'}
                                {...inputPropsSmallTextDebounced}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_tempPassword"
                                label={this.appText('modals.create-invite.temporary-password')}
                                rowClassName={(showNameInputs) ? '' : 'client-invite-hide-name-fields'}
                                value={<span className="invite-temporary-password">{Formatters.getStr(formData, 'tempPassword')}</span>}
                                {...inputPropsLargeTextDebounced}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_matchedUser"
                                label={this.appText('modals.create-invite.username')}
                                value={Formatters.composeUsernameLabel(foundEmail, true)}
                                required={false}
                                rowClassName={(foundEmail) ? '' : 'client-invite-hide-name-fields'}
                                {...inputPropsLargeTextDebounced}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>

                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('assigned-associations')}>
                            {assignedAssociations}
                        </DetailFormCommon.InventoryDetailFormSection>

                        {securityPermissionsSection}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        disabled={!createInviteFormValid}
                        onClick={(e) => this.handleCreateClientInviteButton(e)}
                    >
                        {buttonCaption}
                    </Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalCreateInvite()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { associations, users, associationId, portalUsers, tempPassword, ...rest } = this.props;

        let pageTitle = this.appTextPageTitle();
        let page = (<div />);

        if (_.size(associations) && _.size(users)) {
            const formData = this.getFormData(associations, users, portalUsers, associationId, tempPassword);
            // console.log('pageData', {                
            //     formData: formData,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            const auth = new Auth();
            const isStaff = (auth.isAdmin() || auth.isEmployee()) ? true : false;

            let showSecuritySection = (isStaff) ? true : false;
            // console.log(`create-invite render securitySectionVisisble ${showSecuritySection}`);

            page = (
                <div>
                    {this.renderCreateInviteModal(formData, showSecuritySection)}

                    <Row>
                        <Col xs={12} xl={9}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getNewClientInviteUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getNewClientInviteRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props)
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        users: state.entities.newClientInviteUsers,
        associations: state.entities.newClientInviteAssociations,
        portalUsers: state.entities.newClientInvitePortalUsers,
        associationId: Formatters.getInt(props, 'match.params.associationId'),
        tempPassword: state.entities.newClientInviteTempPassword,
    });
};

const mapDispatchToProps = {
    newClientInvite: MutateActions.newClientInviteMutateAsync,
    navigateTo: (path) => push(path),
    navigateBack: () => goBack(),
};

const CreateInviteContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(CreateInvite);

export default withRouter(CreateInviteContainer);



// WEBPACK FOOTER //
// ./src/invite/create-invite.tsx